import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Edit,
  Trash2,
  Scale,
  AlertTriangle,
  ChefHat,
  Printer
} from 'lucide-react';
import { ScreenshotProtection } from '../components/ScreenshotProtection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Format quantity with up to 5 decimal places, removing trailing zeros
const formatQuantity = (num) => {
  if (num === 0 || num === null || num === undefined) return '0';
  
  // Round to 5 decimal places
  const rounded = Math.round(num * 100000) / 100000;
  
  // Convert to string with up to 5 decimal places
  let result = rounded.toFixed(5);
  
  // Remove trailing zeros but keep at least 2 decimal places for readability
  result = result.replace(/\.?0+$/, '');
  
  // If no decimal point, add .00 for whole numbers > 1, or show as-is for small numbers
  if (!result.includes('.')) {
    if (Math.abs(rounded) >= 1) {
      result = rounded.toFixed(2);
    }
  } else {
    // Ensure at least 2 decimal places for numbers >= 1
    const parts = result.split('.');
    if (Math.abs(rounded) >= 1 && parts[1] && parts[1].length < 2) {
      result = rounded.toFixed(2);
    }
  }
  
  return result;
};

export const RecipeDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    fetchRecipe();
  }, [id]);

  const fetchRecipe = async () => {
    try {
      const response = await axios.get(`${API}/recipes/${id}`);
      setRecipe(response.data);
    } catch (error) {
      toast.error('Failed to fetch recipe');
      navigate('/recipes');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${recipe.recipe_name}"?`)) return;

    try {
      await axios.delete(`${API}/recipes/${recipe.id}`);
      toast.success('Recipe deleted');
      navigate('/recipes');
    } catch (error) {
      toast.error('Failed to delete recipe');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const getCategoryColor = (category) => {
    const colors = {
      Daal: 'bg-orange-500',
      Sabzi: 'bg-green-500',
      Salad: 'bg-cyan-500',
      SpecialMain: 'bg-[#faa023]',
      SpecialRoti: 'bg-[#faa023]',
      SpecialRaita: 'bg-[#faa023]',
      SpecialSide: 'bg-[#faa023]',
      Sweet: 'bg-pink-500',
      Other: 'bg-stone-400'
    };
    return colors[category] || colors.Other;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  if (!recipe) {
    return (
      <div className="text-center py-12">
        <p className="text-stone-500">Recipe not found</p>
      </div>
    );
  }

  return (
    <ScreenshotProtection>
    <div className="max-w-4xl mx-auto" data-testid="recipe-detail-page">
      {/* Header - Hidden on print */}
      <div className="flex items-center gap-4 mb-6 no-print">
        <button
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft size={24} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3">
            <div className={`w-4 h-4 rounded-full flex-shrink-0 ${getCategoryColor(recipe.category)}`} />
            <span className="text-sm font-medium text-stone-500">{recipe.category}</span>
            {!recipe.active && <span className="badge badge-error">Inactive</span>}
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-stone-800 mt-1 truncate">
            {recipe.recipe_name}
          </h1>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={handlePrint}
            className="btn-outline hidden sm:flex"
            data-testid="print-btn"
          >
            <Printer size={18} />
            <span className="hidden md:inline">Print</span>
          </button>
          <button
            onClick={() => navigate(`/recipes/${recipe.id}/edit`)}
            className="btn-primary"
            data-testid="edit-btn"
          >
            <Edit size={18} />
            <span className="hidden md:inline">Edit</span>
          </button>
          <button
            onClick={handleDelete}
            className="p-2.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            data-testid="delete-btn"
            title="Delete recipe"
          >
            <Trash2 size={18} />
          </button>
        </div>
      </div>

      {/* Print Header */}
      <div className="hidden print:block mb-6">
        <h1 className="text-3xl font-bold text-center">{recipe.recipe_name}</h1>
        <p className="text-center text-stone-500">{recipe.category} • {quantity} tiffins</p>
      </div>

      {/* Important Notes */}
      {recipe.important_notes && (
        <div className="virsa-card p-5 mb-6 bg-[#faa023]/10 border-[#faa023]/30">
          <div className="flex items-start gap-3">
            <AlertTriangle size={22} className="text-[#faa023] mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-bold text-stone-800 mb-1">Important Notes</h3>
              <p className="text-stone-700 whitespace-pre-wrap">{recipe.important_notes}</p>
            </div>
          </div>
        </div>
      )}

      {/* Quantity Calculator */}
      <div className="virsa-card p-5 mb-6 bg-[#124242]/5 border-[#124242]/20 no-print">
        <div className="flex items-center gap-4 flex-wrap">
          <Scale size={22} className="text-[#124242]" />
          <span className="text-lg font-medium text-stone-700">Calculate for</span>
          <input
            type="number"
            min="1"
            value={quantity}
            onChange={(e) => {
              const val = e.target.value;
              if (val === '') {
                setQuantity('');
              } else {
                const num = parseInt(val);
                if (!isNaN(num) && num >= 0) {
                  setQuantity(num);
                }
              }
            }}
            onBlur={(e) => {
              // Reset to 1 if empty or 0 when leaving the field
              if (e.target.value === '' || parseInt(e.target.value) < 1) {
                setQuantity(1);
              }
            }}
            className="virsa-input w-28 h-12 text-center text-xl font-bold"
            data-testid="quantity-input"
          />
          <span className="text-lg font-medium text-stone-700">tiffins</span>
        </div>
      </div>

      {/* Ingredients */}
      <div className="virsa-card p-6 mb-6">
        <h2 className="text-xl font-bold text-stone-800 mb-4 flex items-center gap-2">
          <ChefHat size={22} className="text-[#124242]" />
          Ingredients
        </h2>
        
        {recipe.ingredients.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-stone-200">
                  <th className="text-left py-3 px-2 text-sm font-semibold text-stone-600">#</th>
                  <th className="text-left py-3 px-2 text-sm font-semibold text-stone-600">Ingredient</th>
                  <th className="text-right py-3 px-2 text-sm font-semibold text-stone-600">Quantity</th>
                  <th className="text-left py-3 px-2 text-sm font-semibold text-stone-600">Unit</th>
                </tr>
              </thead>
              <tbody>
                {recipe.ingredients.map((ing, idx) => (
                  <tr key={`${ing.ingredient_name}-${ing.unit}`} className="border-b border-stone-100 hover:bg-stone-50">
                    <td className="py-3 px-2 text-stone-400">{idx + 1}</td>
                    <td className="py-3 px-2 font-medium text-stone-800">{ing.ingredient_name}</td>
                    <td className="py-3 px-2 text-right">
                      <span className="text-xl font-bold text-[#124242]">
                        {formatQuantity(ing.qty_per_tiffin * (quantity || 0))}
                      </span>
                    </td>
                    <td className="py-3 px-2 text-stone-600">{ing.unit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-stone-400 italic text-center py-6">No ingredients added</p>
        )}
      </div>

      {/* Cooking Steps */}
      {recipe.cooking_steps && (
        <div className="virsa-card p-6 mb-6">
          <h2 className="text-xl font-bold text-stone-800 mb-4">Cooking Steps</h2>
          <div className="text-stone-700 whitespace-pre-wrap leading-relaxed">
            {recipe.cooking_steps}
          </div>
        </div>
      )}

      {/* Recipe Info Footer */}
      <div className="text-center text-sm text-stone-400 pb-8 no-print">
        <p>
          Created: {new Date(recipe.created_at).toLocaleDateString()} • 
          Updated: {new Date(recipe.updated_at).toLocaleDateString()}
        </p>
      </div>
    </div>
    </ScreenshotProtection>
  );
};
